import React, { useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { useTranslation } from "react-i18next"

import { useAppDispatch, useAppSelector } from "redux/hooks"
import { selectContent, closeDialog } from "redux/features/dialog/dialogSlice"
import {
  selectDebtById,
  selectFetchStatus,
} from "redux/features/debt/debtSlice"
import { fetchDebt } from "redux/features/debt/debtThunk"
import { Progress } from "components/ui/Spinner"
import Close from "components/ui/ModalClose"
import LoadableEntity from "components/ui/LoadableEntity"
import {
  LoaderContainer,
  ErrorContainer,
  Headline,
  Title,
  Description,
} from "hocs/features/modal/elements"
import { DebtType } from "types"

export function withModalDebt<GenericType>(
  WrappedComponent: React.ComponentType<GenericType>,
) {
  const WithModalDebt = (props: GenericType) => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const { t } = useTranslation()

    const content = useAppSelector((state) => selectContent(state))
    const { debtId } = content

    const debt = debtId
      ? useAppSelector((state) => selectDebtById(state, debtId) as DebtType)
      : null

    const fetchStatus = useAppSelector((state) => selectFetchStatus(state))

    useEffect(() => {
      const handleFetchDebt = () => {
        if (!fetchStatus.isFetching && debtId) {
          dispatch(fetchDebt({ debt_id: debtId }))
        }
      }

      handleFetchDebt()
    }, [debtId])

    const handleClose = () => {
      dispatch(closeDialog())
      navigate("/")
    }

    const renderLoader = () => {
      return (
        <LoaderContainer>
          <Progress color="primary" />
        </LoaderContainer>
      )
    }

    const renderError = () => {
      return (
        <ErrorContainer>
          <Close onClick={handleClose} />
          <Headline>
            <Title variant="h3">{t("features.debt.loading.title")}</Title>
            <Title variant="h5">{t("features.debt.loading.error.title")}</Title>
            <Description variant="h6">
              {t("features.debt.loading.error.description")}
            </Description>
          </Headline>
        </ErrorContainer>
      )
    }

    return (
      <LoadableEntity
        entity={debt}
        fetchStatus={fetchStatus}
        renderLoading={renderLoader}
        renderError={renderError}
      >
        {(debt) => (
          <WrappedComponent {...props} debt={debt} fetchStatus={fetchStatus} />
        )}
      </LoadableEntity>
    )
  }

  return WithModalDebt
}
